import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import {
  UnlockOutlined,
} from "@ant-design/icons";
import {
  Button, Checkbox, Empty, Input,
} from "antd";
import Highlighter from "react-highlight-words";
import { toast } from "react-toastify";

import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import TestDirectoryRequestSuccessModal from "./TestDirectoryRequestSuccessModal";

import { canRequestTest } from "../../../utils/Permissions";
import getTestName from "./helpers";

import { createTestRequests } from "../../../actions/testDirectory";

import "./TestDirectoryModals.css";

export default function UnlockTestsPanel({
  directoryTests = [],
  requestedNames = [],
  search = "",
  loading = false,
  onSubmitted,
}) {
  const canSendRequest = canRequestTest();
  const [selected, setSelected] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const requestedSet = useMemo(
    () => new Set(requestedNames.map((name) => String(name).toLowerCase()).filter(Boolean)),
    [requestedNames],
  );

  useEffect(() => {
    setSelected((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((name) => {
        if (requestedSet.has(String(name).toLowerCase())) {
          delete next[name];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [requestedSet]);

  const query = search.trim().toLowerCase();
  const searchedWords = search.trim() ? [search.trim()] : [];

  const requestedRows = useMemo(() => {
    const names = requestedNames.filter(Boolean);
    if (!query) return names;
    return names.filter((name) => name.toLowerCase().includes(query));
  }, [requestedNames, query]);

  const availableRows = useMemo(() => {
    const names = [];
    const seen = new Set();
    directoryTests.forEach((test) => {
      const name = getTestName(test);
      if (!name) return;
      const key = name.toLowerCase();
      if (requestedSet.has(key) || seen.has(key)) return;
      if (query && !key.includes(query)) return;
      seen.add(key);
      names.push(name);
    });
    return names;
  }, [directoryTests, requestedSet, query]);

  const selectedCount = Object.keys(selected).length;
  const selectableCount = availableRows.length;
  const hasSelection = selectedCount > 0;
  const allSelectableSelected = selectableCount > 0 && selectedCount === selectableCount;

  const toggleTest = (name) => {
    if (requestedSet.has(String(name).toLowerCase())) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (name in next) delete next[name];
      else next[name] = "";
      return next;
    });
    setError("");
  };

  const updateUsage = (name, value) => {
    setSelected((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const clearAllSelections = () => {
    setSelected({});
    setError("");
  };

  const handleSubmit = async () => {
    if (!canSendRequest || submitting) return;
    const entries = Object.entries(selected).filter(
      ([name]) => !requestedSet.has(String(name).toLowerCase()),
    );
    if (!entries.length) {
      setError("Select at least one test");
      return;
    }
    const invalid = entries.some(([, usage]) => {
      const value = Number(usage);
      return !String(usage).trim() || !Number.isInteger(value) || value <= 0;
    });
    if (invalid) {
      setError("Enter a valid monthly usage frequency for each selected test");
      return;
    }
    setSubmitting(true);
    const requests = entries.map(([name, usage]) => ({
      test_name: name,
      test_frequency: Number(usage),
      test_spec: "",
      test_unit: "",
      test_method: "",
      comment: "",
      type: "unlock",
    }));
    const { success, message } = await createTestRequests(requests);
    if (!isMountedRef.current) return;
    setSubmitting(false);
    if (success) {
      setSelected({});
      setError("");
      if (typeof onSubmitted === "function") onSubmitted(requests);
      setShowSuccess(true);
      return;
    }
    toast.error(message || "Unable to submit test request.");
  };

  const hasRows = requestedRows.length > 0 || availableRows.length > 0;

  return (
    <>
      <section className="TestDirectory__UnlockCard">
        <div className="TestDirectory__UnlockCardHeader">
          <div className="TestDirectory__UnlockCardHeaderMain">
            <span className="TestDirectory__UnlockCardIconWrap" aria-hidden>
              <UnlockOutlined className="TestDirectory__UnlockCardIcon" />
            </span>
            <div className="TestDirectory__UnlockCardHeaderText">
              <h3 className="TestDirectory__UnlockCardTitle">Unlock More Tests</h3>
              <p className="TestDirectory__UnlockCardSubtitle">
                Requested tests will be available after approval from the Genista CSM team.
              </p>
            </div>
          </div>
          <div className="TestDirectory__UnlockCardHeaderActions">
            <Button
              type="primary"
              className="TestDirectory__UnlockSendBtn"
              htmlType="button"
              disabled={!canSendRequest || !selectedCount || submitting}
              loading={submitting}
              onClick={handleSubmit}
            >
              Send Request
            </Button>
          </div>
        </div>

        <div className="TestDirectory__UnlockTable">
          <div className="TestDirectory__UnlockTableHead">
            <span className="TestDirectory__UnlockColCheck">
              {hasSelection && selectableCount > 0 ? (
                <Checkbox
                  indeterminate={!allSelectableSelected}
                  checked={allSelectableSelected}
                  onChange={clearAllSelections}
                  aria-label="Clear all selections"
                />
              ) : null}
            </span>
            <span className="TestDirectory__UnlockColName TestDirectory__UnlockColName--header">
              <span className="TestDirectory__UnlockTableHeadLabel">Test Name</span>
            </span>
            <span className="TestDirectory__UnlockColUsage TestDirectory__UnlockColUsage--header">
              <span className="TestDirectory__UnlockTableHeadLabel">Monthly Usage Frequency</span>
            </span>
          </div>

          <div
            className={`TestDirectory__UnlockCardBody${!hasRows && !loading ? " TestDirectory__UnlockCardBody--empty" : ""}`}
          >
            {loading ? (
              <div className="TestDirectory__Loading" aria-busy="true">
                <div className="TestDirectory__Skeleton ActiveloadingAnimation" />
              </div>
            ) : (
              <StyledScrollbar
                noScrollX
                autoHide
                className="TestDirectory__UnlockScrollbar"
              >
                {hasRows ? (
                  <div className="TestDirectory__UnlockRows">
                    {requestedRows.map((name) => (
                      <div
                        key={`requested-${name}`}
                        className="TestDirectory__UnlockRow TestDirectory__UnlockRow--requested"
                      >
                        <span className="TestDirectory__UnlockColCheck" aria-hidden />
                        <span className="TestDirectory__UnlockColName TestDirectory__UnlockRowName">
                          <Highlighter
                            highlightClassName="searchHighlight"
                            searchWords={searchedWords}
                            autoEscape
                            textToHighlight={name}
                          />
                          {" "}
                          <span className="TestDirectory__RequestedTag">(Requested)</span>
                        </span>
                        <span className="TestDirectory__UnlockColUsage" />
                      </div>
                    ))}

                    {availableRows.map((name) => {
                      const isSelected = name in selected;
                      return (
                        <div
                          key={name}
                          className={`TestDirectory__UnlockRow${isSelected ? " TestDirectory__UnlockRow--selected" : ""}`}
                        >
                          <span className="TestDirectory__UnlockColCheck">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleTest(name)}
                              aria-label={`Select ${name}`}
                            />
                          </span>
                          <span className="TestDirectory__UnlockColName TestDirectory__UnlockRowName">
                            <Highlighter
                              highlightClassName="searchHighlight"
                              searchWords={searchedWords}
                              autoEscape
                              textToHighlight={name}
                            />
                          </span>
                          <span className="TestDirectory__UnlockColUsage">
                            <Input
                              value={isSelected ? selected[name] : ""}
                              onChange={(e) => updateUsage(name, e.target.value)}
                              placeholder="00"
                              disabled={!isSelected}
                              className={`TestDirectory__UsageInput${isSelected ? " TestDirectory__UsageInput--enabled" : " TestDirectory__UsageInput--disabled"}`}
                              aria-label={`Monthly usage for ${name}`}
                            />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Empty
                    className="TestDirectory__CompactEmpty"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No additional tests available"
                  />
                )}
              </StyledScrollbar>
            )}
          </div>
        </div>
        {error ? (
          <div className="TestDirectory__UnlockCardFooter">
            <small className="TestDirectoryModal__Error TestDirectory__UnlockError">{error}</small>
          </div>
        ) : null}
      </section>

      <TestDirectoryRequestSuccessModal
        open={showSuccess}
        message="The selected test will be added to your test directory once approved."
        onClose={() => setShowSuccess(false)}
      />
    </>
  );
}
